import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";
import { cn } from "utils/cn";
import Icon, { type IconName } from "../AppIcon";

const buttonVariants = cva(
	"inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
	{
		variants: {
			variant: {
				default: "bg-primary text-primary-foreground hover:bg-primary/90",
				destructive:
					"bg-destructive text-destructive-foreground hover:bg-destructive/90",
				outline:
					"border border-input bg-background hover:bg-accent hover:text-accent-foreground",
				secondary:
					"bg-secondary text-secondary-foreground hover:bg-secondary/80",
				ghost: "hover:bg-accent hover:text-accent-foreground",
				link: "text-primary underline-offset-4 hover:underline",
				success: "bg-success text-success-foreground hover:bg-success/90",
				warning: "bg-warning text-warning-foreground hover:bg-warning/90",
				danger: "bg-error text-error-foreground hover:bg-error/90",
			},
			size: {
				default: "h-10 px-4 py-2",
				sm: "h-9 rounded-md px-3",
				lg: "h-11 rounded-md px-8",
				icon: "h-11 w-11", // 44px - meets mobile touch target guidelines
				"icon-sm": "h-9 w-9", // 36px - use sparingly on mobile
				xs: "h-8 rounded-md px-2 text-xs",
				xl: "h-12 rounded-md px-10 text-base",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
		loading?: boolean;
		iconName?: IconName | null;
		iconPosition?: "left" | "right";
		iconSize?: number;
		fullWidth?: boolean;
	};

type ButtonRef = HTMLButtonElement;

const iconSizeMap: Record<NonNullable<ButtonProps["size"]>, number> = {
	xs: 12,
	sm: 14,
	default: 16,
	lg: 18,
	xl: 20,
	icon: 20, // Increased for better visibility in 44px touch target
	"icon-sm": 16, // Increased for better visibility in 36px touch target
};

const LoadingSpinner: React.FC = () => (
	<svg
		className="animate-spin -ml-1 mr-2 h-4 w-4"
		fill="none"
		viewBox="0 0 24 24"
	>
		<circle
			className="opacity-25"
			cx="12"
			cy="12"
			r="10"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<path
			className="opacity-75"
			fill="currentColor"
			d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
		/>
	</svg>
);

const Button = React.forwardRef<ButtonRef, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			asChild = false,
			children,
			loading = false,
			iconName,
			iconPosition = "left",
			iconSize,
			fullWidth = false,
			disabled = false,
			...props
		},
		ref,
	) => {
		const Comp: React.ElementType = asChild ? Slot : "button";

		const resolvedSize = size ?? "default";
		const calculatedIconSize = iconSize ?? iconSizeMap[resolvedSize];

		const renderIcon = () => {
			if (!iconName) return null;

			return (
				<Icon
					name={iconName}
					size={calculatedIconSize}
					className={cn(
						children && iconPosition === "left" && "mr-2",
						children && iconPosition === "right" && "ml-2",
					)}
				/>
			);
		};

		return (
			<Comp
				className={cn(
					buttonVariants({ variant, size }),
					fullWidth && "w-full",
					className,
				)}
				ref={ref}
				disabled={disabled || loading}
				{...props}
			>
				{loading && <LoadingSpinner />}
				{iconPosition === "left" && renderIcon()}
				{children}
				{iconPosition === "right" && renderIcon()}
			</Comp>
		);
	},
);

Button.displayName = "Button";

export default Button;
